import React from "react";
import { motion } from "framer-motion";

const perLocationRows = [
  {
    name: "IAM & Multi-Location Management",
    detail:
      "Standard add-on for groups of 5+ offices. Waived for all 24 Dental 360 locations.",
    strike: "$250",
    discounted: "$0",
    unit: "/ location / month",
    badge: "Waived",
  },
  {
    name: "Onboarding Consulting Package",
    detail:
      "Standard one-time per-location implementation & onboarding consulting package. Waived for all 24 locations.",
    strike: "$3,000",
    discounted: "$0",
    unit: "per location, one-time",
    badge: "Waived",
  },
  {
    name: "SOTA Imaging",
    detail:
      "3 months free for each of the 24 locations, then subsidized to $159 / location / month (standard $199 / location / month).",
    strike: "$199",
    discounted: "$159",
    unit: "/ location / month",
    badge: "+ 3 months free",
  },
];

const Asha360ConcessionsSlide = () => {
  return (
    <div className="h-full w-full bg-[#f8fafc] flex items-center justify-center p-6 xl:p-10 relative overflow-hidden font-sans">
      {/* Ambient gradient blurs */}
      <div
        className="absolute top-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #93c5fd 0%, #c4b5fd 50%, #fbcfe8 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[120px] opacity-20 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #c4b5fd 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full max-w-[1100px] z-10 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-7"
        >
          <h2 className="text-3xl lg:text-[40px] font-bold text-slate-900 tracking-tight leading-tight">
            Special exceptions &amp;{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              discounts.
            </span>
          </h2>
          <p className="text-[14px] text-slate-600 mt-2 max-w-[760px]">
            Contingent on signing a 12-month agreement covering all 24 Dental
            360 &amp; Associates locations.
          </p>
        </motion.div>

        {/* Per-location concessions table */}
        <motion.table
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="w-full"
        >
          <thead>
            <tr className="border-t border-b border-slate-200">
              <th className="text-left py-3 pr-8 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
                Item
              </th>
              <th className="text-right py-3 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 w-[280px]">
                Price per location
              </th>
            </tr>
          </thead>
          <tbody>
            {perLocationRows.map((row) => (
              <tr key={row.name} className="border-b border-slate-200 align-top">
                <td className="py-4 pr-8">
                  <div className="text-[15px] font-semibold text-slate-900 leading-snug">
                    {row.name}
                  </div>
                  <div className="text-[12.5px] text-slate-500 leading-relaxed mt-1 max-w-[720px]">
                    {row.detail}
                  </div>
                </td>
                <td className="py-4 text-right whitespace-nowrap">
                  <div className="flex items-baseline justify-end gap-2 leading-none">
                    <span className="text-[15px] font-medium text-slate-400 line-through">
                      {row.strike}
                    </span>
                    <span className="text-[22px] font-bold text-slate-900 tracking-tight">
                      {row.discounted}
                    </span>
                  </div>
                  <div className="text-[12px] text-slate-500 mt-1.5">
                    {row.unit}
                  </div>
                  {row.badge && (
                    <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-emerald-700 mt-1.5">
                      → {row.badge}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </motion.table>

        {/* Aggregate AI credit package */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.25 }}
          className="mt-7"
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-3">
            Aggregate · across the group
          </p>
          <div className="border-t border-b border-slate-200 py-4 flex items-start justify-between gap-8">
            <div>
              <div className="text-[15px] font-semibold text-slate-900 leading-snug">
                AI credit package
              </div>
              <div className="text-[12.5px] text-slate-500 leading-relaxed mt-1 max-w-[720px]">
                2,000,000 AI credits provided as a one-time aggregate pool,
                pooled across all 24 locations for utilization. (Not per month
                — base AI usage continues at the included $499 / location /
                month baseline rate.)
              </div>
            </div>
            <div className="text-right whitespace-nowrap">
              <div className="text-[22px] font-bold text-slate-900 tracking-tight leading-none">
                2,000,000
              </div>
              <div className="text-[12px] text-slate-500 mt-1.5">
                credits · aggregate
              </div>
              <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-emerald-700 mt-1.5">
                → ~$5,000 of credits · Included
              </div>
            </div>
          </div>
        </motion.div>

        {/* Footnote */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.35 }}
          className="text-[11.5px] text-slate-500 leading-relaxed mt-5 max-w-[920px]"
        >
          <span className="font-semibold text-slate-700">Note:</span>{" "}
          Per-location SOTA Imaging onboarding and optional historical data
          conversion fees may apply, and are paid directly to SOTA.
        </motion.p>
      </div>
    </div>
  );
};

export default Asha360ConcessionsSlide;
